/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import LoadingSpinner from 'src/components/LoadingSpinner'

const translate = t as (key: string, options?: unknown) => string

const overlayStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: ${colors.NEUTRAL_70};
`

/*
 * `LoadingSpinner` is `width: 100%` and paints its circle as an absolutely
 * positioned `:after` centred in that box — it is built to be a block-level,
 * self-centring loader. Dropped into a flex row it stretches, so the `gap` above
 * ends up between the TEXT and the box's EDGE rather than the circle, leaving
 * the circle to drift and sit flush against the label. Bounding it to a square
 * the size of the circle makes the gap mean what it says.
 *
 * Inline rather than a `css` class: that `width: 100%` lives on
 * `.loading-spinner` in SCSS, so an emotion class would tie on specificity and
 * the winner would come down to stylesheet injection order.
 */
const SPINNER_STYLE = { width: '2em', flex: 'none' } as const

/**
 * AG Grid's stock overlays are unlocalized English baked into the library. These
 * two replace them with the app's own spinner and the shared `common:` strings,
 * so a grid never shows untranslated chrome on the fr build.
 */
export const DataGridLoadingOverlay = () => (
  <div css={overlayStyle} role='status' data-testid='data-grid-loading'>
    <LoadingSpinner size={2} style={SPINNER_STYLE} />
    <span>{translate('common:loading')}</span>
  </div>
)

export interface DataGridNoRowsOverlayProps {
  /**
   * Supplied by the grid's `noRowsOverlayComponentParams`.
   *
   * A component always beats `overlayNoRowsTemplate`, so a grid that set the
   * template got the generic string and no warning. This is the seam that lets
   * a grid say what IT is empty of — "No games found" rather than "No results".
   */
  message?: string
}

export const DataGridNoRowsOverlay = ({
  message,
}: DataGridNoRowsOverlayProps) => (
  <div css={overlayStyle} data-testid='data-grid-no-rows'>
    <span>{message ?? translate('common:noResults')}</span>
  </div>
)
